import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Wrench, User, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

const CryptoDalalProject = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const keyChallenges = [
    { title: "Knowledge Deficit", desc: "Most Indians can't explain blockchain or differentiate cryptocurrencies despite active investments" },
    { title: "Trust Crisis", desc: "Volatility and regulatory uncertainty generate deep skepticism among potential investors" },
    { title: "Speculative Behavior", desc: "FOMO-driven decisions rather than strategic allocation dominate investment patterns" },
  ];

  const researchInsights = [
    {
      title: "Critical Finding #1: Knowledge Deficit",
      description: "Participants showed superficial understanding despite active interest. Most couldn't explain fundamentals or articulate strategies beyond 'buy and hold.'",
    },
    {
      title: "Critical Finding #2: Speculation Mindset",
      description: "Millennials exhibit 'get-rich-quick' mentalities, viewing crypto as rapid wealth vehicles rather than strategic assets.",
    },
    {
      title: "Critical Finding #3: Disposable Income Psychology",
      description: "Investors allocate only 'extra' funds they can afford to lose, indicating limited confidence despite enthusiasm.",
    },
  ];

  const investorBenefits = [
    { title: "Expert Knowledge Access", desc: "Without deep research requirements" },
    { title: "Risk Mitigation", desc: "Through professional management" },
    { title: "Real-Time Transparency", desc: "And control over investments" },
    { title: "Educational Learning", desc: "Through expert observation" },
  ];

  const expertBenefits = [
    { title: "Client Acquisition", desc: "Without marketing infrastructure" },
    { title: "Scalable Revenue", desc: "Managing multiple portfolios" },
    { title: "Performance Showcase", desc: "Attracting quality clients" },
  ];

  const userScreens = [
    {
      number: "01",
      title: "Dashboard",
      description: "A snapshot of your investment returns, current market position, and daily crypto news. Tap the Initiate button to begin investing.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3A35c272a9-da12-47a7-b99d-068df50c7276%3AGroup_82.png?table=block&id=30f22ee1-b108-8007-b5ec-cef9cf4326b1&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1060&userId=&cache=v2",
    },
    {
      number: "02",
      title: "Portfolio",
      description: "A granular breakdown of your active holdings and ongoing investments, complete with detailed statistics. Withdraw funds at any point as needed.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3A9e5af229-c6ad-4048-873c-f84203f32ff9%3AGroup_83.png?table=block&id=30f22ee1-b108-802e-b35f-eb5b48e77506&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1060&userId=&cache=v2",
    },
    {
      number: "03",
      title: "Add Money",
      description: "Add funds to your wallet seamlessly using multiple payment methods, including debit card and net banking.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3A6e167202-56ad-4223-91dc-2de2f6c832a0%3AGroup_84.png?table=block&id=30f22ee1-b108-80f0-80e0-d182cd5f54e1&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1060&userId=&cache=v2",
    },
    {
      number: "04",
      title: "Transactions",
      description: "A comprehensive log of all placed trades, detailing the token, transaction type, and quantity involved in each order.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3Aad34586b-17e4-470a-a270-d05fdfbd7978%3AGroup_85.png?table=block&id=30f22ee1-b108-8099-aad3-e69b4d16e076&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1060&userId=&cache=v2",
    },
  ];

  const expertScreens = [
    {
      number: "01",
      title: "Expert Dashboard",
      description: "A consolidated view of all client portfolios under management, highlighting new requests, active investments, and their corresponding returns.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3Ac110d0e7-3400-466a-b972-babf5d6fa8b1%3AGroup_86.png?table=block&id=30f22ee1-b108-8010-abe3-ff79aeb59828&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1060&userId=&cache=v2",
    },
    {
      number: "02",
      title: "Clients",
      description: "A at-a-glance summary of each client's portfolio status, recent returns, and pending investment intentions. Tap the action button to begin investing on their behalf.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3A0801edba-dbc2-49b8-bb30-10feb576b97a%3AGroup_87.png?table=block&id=30f22ee1-b108-8055-90c4-e0ad07f3dc8e&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1060&userId=&cache=v2",
    },
    {
      number: "03",
      title: "Trading",
      description: "An integrated trading environment for experts, powered by a third-party platform.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3Af86db6ad-4531-454f-9916-1a35f2c27bcf%3AGroup_88.png?table=block&id=30f22ee1-b108-80d5-b5c9-f8b724be97b1&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1060&userId=&cache=v2",
    },
    {
      number: "04",
      title: "Transactions",
      description: "A full audit of all trades executed by the expert on behalf of their clients, displaying the specific crypto tokens transacted, along with the investment amount and returns.",
      image: "https://phrygian-tornado-507.notion.site/image/attachment%3A68690cbf-b650-45cd-9f07-75fa8f69c845%3AGroup_89.png?table=block&id=30f22ee1-b108-804a-84d2-f0bceb4eeabd&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=1060&userId=&cache=v2",
    },
  ];

  const designDecisions = [
    { title: "Simplicity Over Complexity", desc: "Interface prioritizes clarity, using progressive disclosure to prevent overwhelming users." },
    { title: "Data-Driven Trust", desc: "Transparent metrics, credentials, and trade histories address trust deficits through evidence." },
    { title: "Professional Visual Language", desc: "Design communicates security and legitimacy, balancing approachability with authority." },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed top-6 left-6 z-50"
      >
        <Button
          onClick={() => navigate("/#projects")}
          variant="outline"
          className="no-shiny h-12 px-4 rounded-full border-white/10 bg-black/50 backdrop-blur-xl text-white hover:bg-white/10"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
      </motion.div>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-6 bg-purple-500/10 text-purple-400 border-purple-500/20">
              Crypto
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] text-white mb-6">
              CryptoDalal
            </h1>
            <p className="text-xl md:text-2xl text-white/60 max-w-3xl mb-10">
              Bridging India's Cryptocurrency Knowledge Gap Through Expert Portfolio Management
            </p>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-6 mb-12">
              <div className="flex items-center gap-2 text-white/50">
                <User className="w-5 h-5" />
                <span>UX Researcher & Product Designer</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Calendar className="w-5 h-5" />
                <span>4 Weeks (Capstone)</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Wrench className="w-5 h-5" />
                <span>Figma, Miro, User Interviews</span>
              </div>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="rounded-3xl overflow-hidden border border-white/5"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_web-creator-900/artifacts/plis2unz_Gemini_Generated_Image_j4dxuoj4dxuoj4dx.png"
              alt="CryptoDalal Platform"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Design Challenge */}
      <section className="py-20 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              Design Challenge
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white leading-tight max-w-4xl">
              How might we create a crypto asset management platform connecting everyday investors with verified experts who manage portfolios, while building trust in a market characterized by volatility and knowledge gaps?
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Problem Space */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              Problem Space & Market Context
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
              The Indian Crypto Landscape
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              Cryptocurrency has gained explosive interest in India, particularly among millennials. However, enthusiasm coexists with confusion, misinformation, and fear. Most Indians lack fundamental understanding despite active investments, creating vulnerability to poor decisions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {keyChallenges.map((challenge, index) => (
              <motion.div
                key={challenge.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{challenge.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{challenge.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Research */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              Research Methodology
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              User Research & Surveys
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl overflow-hidden border border-white/5"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3Ac9236b29-12b1-4da5-8028-49853b5281c5%3Aimage_3.png?table=block&id=30d22ee1-b108-8002-92af-c8436de4034d&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                alt="Survey - What does crypto mean"
                className="w-full"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-3xl overflow-hidden border border-white/5"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3A03b6418d-5a99-4a51-8619-9c9451f27851%3Aimage_6.png?table=block&id=30d22ee1-b108-8067-acdf-cb71048fb293&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                alt="Survey - Understanding"
                className="w-full"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="rounded-3xl overflow-hidden border border-white/5"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3Ab714cc5d-55bb-4d64-bc40-63be4697921d%3Aimage_8.png?table=block&id=30d22ee1-b108-801d-aa1f-ea5b467e94c7&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                alt="Survey - Trust"
                className="w-full"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="rounded-3xl overflow-hidden border border-white/5"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3Ab6a5d36d-cf05-4b62-8ffe-1414368ca5f8%3AGroup_79.png?table=block&id=30d22ee1-b108-8084-abc2-daa13ac90395&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
                alt="Survey - Investment drivers"
                className="w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Product Landscape */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              Competitive Analysis
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
              Product Landscape
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              Surveyed existing crypto platforms to identify market gaps and user expectations. Analysis revealed that while exchanges offer trading tools, none provide expert-managed portfolio services with transparent fee structures.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl overflow-hidden bg-white p-3"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3Afd599745-fd09-4ebb-9bbc-a13313f63cfb%3Aimage_14.png?table=block&id=30d22ee1-b108-80db-a556-d2f8dc8cb196&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2"
                alt="WazirX"
                className="w-full rounded-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl overflow-hidden bg-white p-3"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3A1dd6a961-182b-4636-9809-69d45448ba72%3Aimage_15.png?table=block&id=30d22ee1-b108-8028-aed0-d4a8ac7e0eab&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2"
                alt="CoinDCX"
                className="w-full rounded-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl overflow-hidden bg-white p-3"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3Aa6f3037f-e176-41e0-abda-114ae64d8164%3Aimage_17.png?table=block&id=30d22ee1-b108-80be-a353-fb536e43dde6&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2"
                alt="CoinSwitch Kuber"
                className="w-full rounded-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="rounded-2xl overflow-hidden bg-white p-3"
            >
              <img
                src="https://phrygian-tornado-507.notion.site/image/attachment%3A82fdf1d4-d284-45ba-ab24-029377a0b6d4%3Aimage_13.png?table=block&id=30d22ee1-b108-8095-abdc-ff4d4e395243&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=480&userId=&cache=v2"
                alt="ZebPay"
                className="w-full rounded-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Research Insights */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              Research Insights
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Critical Findings
            </h2>
          </motion.div>

          <div>
            {researchInsights.map((insight, index) => (
              <motion.div
                key={insight.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-8 rounded-2xl bg-white/5 border border-white/5${index > 0 ? ' mt-8' : ''}`}
              >
                <h3 className="text-xl font-bold font-['Syne'] text-white mb-4">
                  {insight.title}
                </h3>
                <p className="text-white/60 leading-relaxed">{insight.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-12 rounded-3xl overflow-hidden border border-white/5"
          >
            <img
              src="https://phrygian-tornado-507.notion.site/image/attachment%3Ad678ff32-b20a-4502-a173-164390384d0d%3Aimage_11.png?table=block&id=30d22ee1-b108-800b-af59-f24b4d207679&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
              alt="Desk Research"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              The Solution
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
              Expert-Managed Portfolios
            </h2>
            <p className="text-lg text-white/60 max-w-3xl mx-auto">
              CryptoDalal connects investors with verified crypto experts who actively manage portfolios, democratizing professional management while providing transparency and education.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold font-['Syne'] text-white mb-6">For Investors</h3>
              <div className="grid grid-cols-2 gap-4">
                {investorBenefits.map((benefit, index) => (
                  <div key={benefit.title} className="p-4 rounded-xl bg-white/5 border border-white/5 h-full">
                    <h4 className="text-base font-bold text-white mb-2">{benefit.title}</h4>
                    <p className="text-white/50 text-sm">{benefit.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-xl font-bold font-['Syne'] text-white mb-6">For Experts</h3>
              <div className="grid grid-cols-2 gap-4">
                {expertBenefits.map((benefit, index) => (
                  <div key={benefit.title} className="p-4 rounded-xl bg-white/5 border border-white/5 h-full">
                    <h4 className="text-base font-bold text-white mb-2">{benefit.title}</h4>
                    <p className="text-white/50 text-sm">{benefit.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Information Architecture */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              Design Process
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Information Architecture
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl overflow-hidden bg-[#121212] border border-white/5"
          >
            <img
              src="https://phrygian-tornado-507.notion.site/image/attachment%3A880f3a8d-985d-4b37-90f2-d1fcbbb40cfd%3ACryptoDalal_1.png?table=block&id=30e22ee1-b108-8069-8e5c-e7a66e0012f5&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
              alt="Information Architecture"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* User Interface */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              Key Screens
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              User Side Interface
            </h2>
          </motion.div>

          <div>
            {userScreens.map((screen, index) => (
              <motion.div
                key={screen.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid md:grid-cols-5 gap-10 items-center${index > 0 ? ' mt-16' : ''}`}
              >
                <div className={`md:col-span-3 ${index % 2 === 1 ? "md:order-2" : ""}`}>
                  <div className="rounded-2xl overflow-hidden border border-white/5">
                    <img src={screen.image} alt={screen.title} className="w-full" />
                  </div>
                </div>
                <div className={`md:col-span-2 ${index % 2 === 1 ? "md:order-1" : ""}`}>
                  <span className="text-5xl font-bold text-white/10 font-['Syne']">{screen.number}</span>
                  <h3 className="text-2xl font-bold font-['Syne'] text-white mt-2 mb-4">
                    {screen.title}
                  </h3>
                  <p className="text-white/60 leading-relaxed">{screen.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expert Interface */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              Key Screens
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Expert Side Interface
            </h2>
          </motion.div>

          <div>
            {expertScreens.map((screen, index) => (
              <motion.div
                key={screen.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid md:grid-cols-5 gap-10 items-center${index > 0 ? ' mt-16' : ''}`}
              >
                <div className={`md:col-span-3 ${index % 2 === 1 ? "md:order-2" : ""}`}>
                  <div className="rounded-2xl overflow-hidden border border-white/5">
                    <img src={screen.image} alt={screen.title} className="w-full" />
                  </div>
                </div>
                <div className={`md:col-span-2 ${index % 2 === 1 ? "md:order-1" : ""}`}>
                  <span className="text-5xl font-bold text-white/10 font-['Syne']">{screen.number}</span>
                  <h3 className="text-2xl font-bold font-['Syne'] text-white mt-2 mb-4">
                    {screen.title}
                  </h3>
                  <p className="text-white/60 leading-relaxed">{screen.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategic Design Decisions */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              Design Philosophy
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Strategic Design Decisions
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {designDecisions.map((decision, index) => (
              <motion.div
                key={decision.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{decision.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{decision.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reflections */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-b from-[#0d0d0d] to-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              Reflections
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
              Key Learning
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              Primary research revealed that trust crisis—not technology—blocks adoption. Without user conversations, design would have focused on features rather than trust-building. For financial platforms, trust isn't secondary—it's the primary product.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-purple-400 tracking-wider uppercase mb-4">
              Conclusion
            </p>
            <h2 className="text-2xl md:text-3xl font-bold font-['Syne'] text-white mb-6 leading-relaxed">
              CryptoDalal addresses India's cryptocurrency gap by providing professional management at accessible price points. By connecting novices with experts, the platform accelerates learning while reducing risk, demonstrating how research uncovers emotional barriers functional solutions can't address.
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Button
              onClick={() => navigate("/#projects")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Portfolio
            </Button>
            
            <Button
              onClick={() => navigate("/#contact")}
              className="h-12 px-8 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-all"
            >
              Get in Touch
            </Button>
            
            <Button
              onClick={() => navigate("/projects/bare-organic")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
            >
              Next: Bare Organic
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CryptoDalalProject;
